import React, { Fragment, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import axios from 'axios';

import styles from './ContactUsForm.module.scss';
import content from './ContactUsForm.json';
import { API } from '@constants';
import { hideModal } from '@store/appStateSlice';
import { getLocalizedContent as l } from '@helpers/languages';
import Textarea from '@common/components/Forms/components/Textarea';
import Button from '@common/components/Forms/components/Button';
import Notification from '@common/components/Forms/components/Notification';

// TODO: Make work with order
const ContactUsForm = ({ configurationData }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, errors } = useForm({
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });
  const [submitStatus, setSubmitStatus] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notificationContent, setNotificationContent] = useState(null);
  const {
    appState: { language },
    userProfile,
  } = useSelector(state => state);
  const { email, firstName, lastName, primaryBilling, newsLetterOptIn } =
    userProfile?.user ?? {};
  const currentReservation = configurationData.reservation;

  const onSubmit = async formData => {
    try {
      setProcessing(true);

      const company =
        firstName || lastName ? `${firstName || ''}${lastName || ''}` : null;
      const { error } = await axios.post(`${API.FORMS}`, {
        firstName: firstName || null,
        lastName: lastName || null,
        email: email,
        company: company || null,
        country: primaryBilling.country || null,
        zipcode: primaryBilling.zipcode || null,
        questionDesc: formData.comment || null,
        source: 'Website',
        captureForm: 'Reservation Contact Form',
        sourcePlatform: window?.navigator?.platform || null,
        sourceTimeZone:
          Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
        preferredLanguage: language || null,
        language: window?.navigator?.language || null,
        newsLetterOptOut: newsLetterOptIn || false,
        sfResDataId: currentReservation.sfResDataId,
      });

      if (!error) {
        setSubmitStatus(true);
        setProcessing(false);
      } else {
        throw new Error('Uncaught error');
      }
    } catch (error) {
      setProcessing(false);

      if (error?.response?.status === 400) {
        setNotificationContent({
          type: 'error',
          message: error?.response?.data?.statusMessage,
        });
      } else {
        setNotificationContent({
          type: 'error',
          message: l(content.tryLater, language),
        });
      }
    }
  };

  return (
    <div className={styles.ContactUsForm}>
      <h4>{l(content.heading, language)}</h4>
      {submitStatus ? (
        <p>{l(content.thankyou, language)}</p>
      ) : (
        <Fragment>
          <p>
            {l(content.message, language)}
            <br />
            {l(content.reply, language)} <span>&quot;{email}&quot;</span>
          </p>

          <AnimatePresence>
            {notificationContent ? (
              <motion.div
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                transition={{ duration: 0.75 }}
              >
                <Notification
                  type={notificationContent.type}
                  message={notificationContent.message}
                  language={language}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <Textarea
              id="comment"
              name="comment"
              register={register({
                required: {
                  value: true,
                  message: l(content.errorMessage, language),
                },
              })}
              errors={errors}
              label={l(content.label, language)}
              placeholder={l(content.placeholder, language)}
              rows="6"
              autoComplete="off"
            />
            <Button
              type="submit"
              text={l(content.button, language)}
              style={{ marginBottom: '16px' }}
              disabled={processing}
              disabledText={l(content.processing, language)}
              language={language}
            />
          </form>
          <a onClick={() => dispatch(hideModal())}>
            {l(content.cancel, language)}
          </a>
        </Fragment>
      )}
    </div>
  );
};

export default ContactUsForm;
